<template>
  <div class="data-table-container">
    <!-- Search and Filters -->
    <div v-if="!hideSearch" class="filters-container mb-4">
      <div class="search-container">
        <div class="input-group">
          <input
              type="text"
              class="form-control search-input"
              :placeholder="`${title} durchsuchen...`"
              v-model="searchQuery"
              @input="debounceSearch"
          >
          <button v-if="searchQuery" class="btn btn-outline-secondary" @click="clearSearch" type="button">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="entries-selector">
        <div class="d-flex align-items-center">
          <label class="me-2">Einträge pro Seite:</label>
          <select class="form-select entries-select" v-model="perPage" @change="loadData">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-responsive">
      <table class="table table-striped table-hover">
        <thead>
        <tr>
          <th
              v-for="column in columns"
              :key="column.key"
              @click="column.sortable ? sort(column.key) : null"
              :class="{
                'cursor-pointer': column.sortable,
                'sorted': sortField === column.key
              }"
          >
            {{ column.label }}
            <i
                v-if="column.sortable && sortField === column.key"
                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"
                class="sort-icon"
            ></i>
            <i
                v-else-if="column.sortable"
                class="fas fa-sort sort-icon-inactive"
            ></i>
          </th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="item in data" :key="item.id" class="data-row" :class="rowClass ? rowClass(item) : ''">
          <td v-for="column in columns" :key="column.key" :class="[cellClass ? cellClass(column, item) : '', 'cell-' + (column.align || 'center')]">
            <!-- Use router-link for the auftrag_text column -->
            <router-link
                v-if="column.key === 'auftrag_text'"
                :to="{ name: 'auftrag-show', params: { id: item.id }}"
                class="auftrag-link"
            >
              {{ formatValue(item[column.key], column.type) }}
            </router-link>
            <!-- Use default slot for other columns -->
            <slot v-else :name="column.key" :item="item" :value="item[column.key]">
              {{ formatValue(item[column.key], column.type) }}
            </slot>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Lädt...</span>
      </div>
    </div>

    <!-- No Data -->
    <div v-if="!loading && data.length === 0" class="no-data">
      Keine Daten gefunden
    </div>

    <!-- Pagination -->
    <nav v-if="totalPages > 1" class="pagination-container">
      <ul class="pagination">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" @click.prevent="changePage(currentPage - 1)" href="#">
            <i class="fas fa-chevron-left"></i>
          </a>
        </li>

        <li v-if="currentPage > 3" class="page-item">
          <a class="page-link" @click.prevent="changePage(1)" href="#">1</a>
        </li>

        <li v-if="currentPage > 3" class="page-item disabled">
          <span class="page-link">...</span>
        </li>

        <li
            v-for="page in visiblePages"
            :key="page"
            class="page-item"
            :class="{ active: page === currentPage }"
        >
          <a class="page-link" @click.prevent="changePage(page)" href="#">{{ page }}</a>
        </li>

        <li v-if="currentPage < totalPages - 2" class="page-item disabled">
          <span class="page-link">...</span>
        </li>

        <li v-if="currentPage < totalPages - 2" class="page-item">
          <a class="page-link" @click.prevent="changePage(totalPages)" href="#">{{ totalPages }}</a>
        </li>

        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" @click.prevent="changePage(currentPage + 1)" href="#">
            <i class="fas fa-chevron-right"></i>
          </a>
        </li>
      </ul>
    </nav>

    <div class="table-info">
      <span>Zeige {{ (currentPage - 1) * perPage + 1 }} bis {{ Math.min(currentPage * perPage, totalCount) }} von {{ totalCount }} Einträgen</span>
    </div>
  </div>
</template>

<script>
import { formatNumberSmart } from '@/utils/numberFormat'

export default {
  name: 'DataTable',

  props: {
    title: String,
    endpoint: String,
    columns: Array,
    data: Array,
    loading: Boolean,
    initialSearch: { type: String, default: '' },
    currentPage: { type: Number, default: 1 },
    totalPages:  { type: Number, default: 1 },
    totalCount:  { type: Number, default: 0 },
    rowClass: { type: Function, default: null },
    // Optional per-cell class hook: (column, item) => class. Used e.g. to tint
    // the Status cell background with the status colour instead of a badge.
    cellClass: { type: Function, default: null },
    hideSearch: { type: Boolean, default: false }
  },

  data() {
    return {
      searchQuery: this.initialSearch || '',
      searchTimeout: null,
      perPage: 25,
      sortField: '',
      sortDirection: 'asc'
    }
  },

  computed: {
    visiblePages() {
      const pages = []
      let start = Math.max(1, this.currentPage - 1)
      let end = Math.min(this.totalPages, this.currentPage + 1)

      // Ensure we always have 3 page numbers if possible
      if (end - start + 1 < 3) {
        if (start === 1) {
          end = Math.min(this.totalPages, end + 1)
        } else if (end === this.totalPages) {
          start = Math.max(1, start - 1)
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    }
  },

  methods: {
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.search()
      }, 300)
    },

    search() {
      this.$emit('search', this.searchQuery)
    },

    clearSearch() {
      this.searchQuery = ''
      this.$emit('search', '')
    },

    sort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'asc'
      }

      this.$emit('sort', {field: this.sortField, direction: this.sortDirection})
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit('page-change', page)
      }
    },

    loadData() {
      this.$emit('per-page-change', this.perPage)
    },

    formatValue(value, type) {
      if (value === null || value === undefined || value === '') return '-'

      switch (type) {
        case 'date':
          return new Date(value).toLocaleDateString('de-DE')
        case 'currency':
          return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
          }).format(value)
        case 'number':
          // Smart: preserves non-numeric annotations ("50x") instead of NaN.
          return formatNumberSmart(value) || '-'
        default:
          return value
      }
    }
  }
}
</script>

<style scoped>
.data-table-container {
  font-family: 'Inter', sans-serif;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 0 15px;
}

.filters-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
}

.search-container {
  flex: 1;
  min-width: 250px;
  position: relative;
}

.search-hint {
  position: absolute;
  left: 0;
  bottom: -20px;
  color: #6c757d;
  font-size: 0.75rem;
}

.search-input {
  border-radius: 6px 0 0 6px;
  border: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  height: 42px;
  font-size: 0.9rem;
}

.btn-search {
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 0 6px 6px 0;
  height: 42px;
  width: 42px;
}

.entries-selector {
  min-width: 180px;
}

.entries-select {
  height: 42px;
  border-radius: 6px;
  font-size: 0.9rem;
  border: 1px solid #dee2e6;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
  font-size: 0.9rem;
}

thead {
  background-color: #f8f9fa;
}

th {
  font-weight: 600;
  /* Boss-Mail 2: kompakter + Überschriften mittig + zweizeilige Köpfe (\n). */
  padding: 0.45rem 0.5rem;
  color: #495057;
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  position: sticky;
  top: 0;
  z-index: 10;
  text-align: center;
  white-space: pre-line;
  line-height: 1.15;
}

th.sorted {
  background-color: #f1f3f5;
  color: #000;
}

.sort-icon {
  margin-left: 0.5rem;
  color: #000;
}

.sort-icon-inactive {
  margin-left: 0.5rem;
  color: #adb5bd;
  opacity: 0.5;
}

tbody tr {
  transition: background-color 0.15s ease;
}

tbody tr:hover {
  background-color: #f8f9fa;
}

td {
  /* Boss-Mail 2: kompaktere Zeilen (geringere Höhe). */
  padding: 0.18rem 0.5rem;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

/* Boss-Mail 2: Spaltenausrichtung je Spalte (Standard mittig; Kunde/
   Beschreibung/Papier links; Auflage/Gewicht rechts). */
td.cell-left   { text-align: left; }
td.cell-center { text-align: center; }
td.cell-right  { text-align: right; }

.data-row:last-child td {
  border-bottom: none;
}

.cursor-pointer {
  cursor: pointer;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 3rem 0;
}

.no-data {
  display: flex;
  justify-content: center;
  padding: 3rem 0;
  color: #6c757d;
  font-style: italic;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.pagination {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
}

.page-item {
  margin: 0 0.2rem;
}

.page-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  color: #495057;
  text-decoration: none;
  transition: all 0.15s ease;
  font-size: 0.9rem;
}

.page-item.active .page-link {
  background-color: #000;
  color: #fff;
  border-color: #000;
}

.page-item:not(.active):not(.disabled) .page-link:hover {
  background-color: #f8f9fa;
}

.page-item.disabled .page-link {
  color: #adb5bd;
  pointer-events: none;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.table-info {
  margin-top: 1rem;
  color: #6c757d;
  font-size: 0.85rem;
  text-align: center;
}

/* Auftrag link styling */
.auftrag-link {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

.auftrag-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .filters-container {
    flex-direction: column;
  }

  .search-container,
  .entries-selector {
    width: 100%;
  }

  th, td {
    padding: 0.5rem;
  }
}
</style>
